import React, { useState, useRef, KeyboardEvent } from 'react';
import { useChat } from '../contexts/ChatContext';
import { sendMessage } from '../services/api';
import { Message } from '../types';
import './MessageInput.css';

interface MessageInputProps {
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({ disabled = false }) => {
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { dispatch } = useChat();

  const handleSubmit = async () => {
    if (!inputValue.trim() || disabled) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      role: 'user',
      timestamp: new Date(),
    };

    // 添加用户消息
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    
    // 清空输入框
    setInputValue('');
    
    // 重置文本框高度
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // 设置加载状态
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      // 发送API请求
      const response = await sendMessage(userMessage.content);
      
      if (response.success && response.data) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: response.data.message,
          role: 'assistant',
          timestamp: new Date(response.data.timestamp),
        };
        
        dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
      } else {
        throw new Error(response.error || '未知错误');
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // 自动调整高度
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  return (
    <div className="message-input">
      <div className="input-container">
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="给 Azure AI 助手发送消息..."
          disabled={disabled}
          rows={1}
          maxLength={2000}
        />
        <button
          onClick={handleSubmit}
          disabled={disabled || !inputValue.trim()}
          className="send-button"
          title="发送消息"
        >
          <span className="send-icon">➤</span>
        </button>
      </div>
      <div className="input-footer">
        <span className="char-count">
          {inputValue.length}/2000
        </span>
      </div>
    </div>
  );
};

export default MessageInput; 